import React from 'react';
// ─── Bubble Field ────────────────────────────────────────────────────────────
export interface BubbleFieldProps {
  tone?: 'light' | 'dark';
}
export function BubbleField({
  tone = 'dark'
}: BubbleFieldProps) {
  const bubbles = [{
    size: 10,
    left: '8%',
    delay: '0s',
    duration: '8s'
  }, {
    size: 18,
    left: '19%',
    delay: '-4s',
    duration: '11s'
  }, {
    size: 7,
    left: '31%',
    delay: '-2s',
    duration: '7s'
  }, {
    size: 22,
    left: '48%',
    delay: '-7s',
    duration: '12s'
  }, {
    size: 12,
    left: '62%',
    delay: '-1s',
    duration: '9s'
  }, {
    size: 16,
    left: '74%',
    delay: '-5s',
    duration: '10s'
  }, {
    size: 8,
    left: '87%',
    delay: '-3s',
    duration: '7s'
  }, {
    size: 25,
    left: '94%',
    delay: '-8s',
    duration: '13s'
  }, {
    size: 14,
    left: '40%',
    delay: '-6s',
    duration: '10s'
  }, {
    size: 30,
    left: '55%',
    delay: '-10s',
    duration: '14s'
  }, {
    size: 11,
    left: '68%',
    delay: '-9s',
    duration: '8s'
  }];
  return <div className={`deck-bubble-field deck-bubble-field--${tone}`} aria-hidden="true" data-id="element-408" data-mp="1dp2byybomegi">
      {bubbles.map((b, i) => <span key={i} className="deck-bubble" style={{
      width: `${b.size}px`,
      height: `${b.size}px`,
      left: b.left,
      animationDelay: b.delay,
      animationDuration: b.duration
    }} data-id="element-409" data-mp="l0y4c79jvrb6" data-mpi={i} />)}
    </div>;
}
// ─── Slide Label ─────────────────────────────────────────────────────────────
export interface SlideLabelProps {
  number: number;
  title: string;
  dark?: boolean;
}
export function SlideLabel({
  number,
  title,
  dark = false
}: SlideLabelProps) {
  return <div className="flex items-center gap-3 mb-4" data-id="element-410" data-mp="21f6gd7wp2i8k">
      <span className={`text-[11px] font-mono px-2 py-0.5 rounded border ${dark ? 'border-blue-400/50 bg-blue-400/10 text-blue-300' : 'border-blue-200 bg-blue-50 text-blue-700'}`} data-id="element-411" data-mp="y5241njlm1ke">
        {String(number).padStart(2, '0')}
      </span>
      <span className="text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-400" data-id="element-412" data-mp="180luvvjb2a2a">
        {title}
      </span>
    </div>;
}
// ─── Metric Card ─────────────────────────────────────────────────────────────
export interface MetricCardProps {
  value: string;
  label: string;
  detail?: string;
  dark?: boolean;
}
export function MetricCard({
  value,
  label,
  detail,
  dark = false
}: MetricCardProps) {
  return <div className={`deck-stagger-item rounded-xl border p-4 ${dark ? 'border-white/15 bg-white/5' : 'border-slate-200 bg-white shadow-sm'}`} data-id="element-413" data-mp="t57a1b4j0vv4">
      <div className={`text-2xl font-bold tracking-tight ${dark ? 'text-white' : 'text-navy-950'}`} data-id="element-414" data-mp="3ssfk99cext48">
        {value}
      </div>
      <div className={`mt-1 text-sm font-semibold ${dark ? 'text-slate-200' : 'text-slate-700'}`} data-id="element-415" data-mp="3parleusyxwm7">
        {label}
      </div>
      {detail && <div className={`mt-1 text-xs leading-relaxed ${dark ? 'text-slate-400' : 'text-slate-500'}`} data-id="element-416" data-mp="3kcq0s5fw9p8m">
          {detail}
        </div>}
    </div>;
}
// ─── Bar Chart ───────────────────────────────────────────────────────────────
export interface BarDatum {
  label: string;
  value: number;
  formatted: string;
}
export function BarChart({
  data,
  dark = false
}: {
  data: BarDatum[];
  dark?: boolean;
}) {
  const maxValue = Math.max(...data.map(d => d.value));
  return <div className="space-y-2.5" data-id="element-417" data-mp="2wk9jvy7w83aw">
      {data.map(item => <div key={item.label} className="flex items-center gap-3" data-id="element-418" data-mp="2slsb7a8tqkrd">
          <span className={`w-12 shrink-0 text-right text-[11px] font-medium ${dark ? 'text-slate-400' : 'text-slate-500'}`} data-id="element-419" data-mp="2nledal0xsr72">
            {item.label}
          </span>
          <div className={`h-6 flex-1 overflow-hidden rounded-full ${dark ? 'bg-white/10' : 'bg-slate-100'}`} data-id="element-420" data-mp="mjgbvmxr121m">
            <div className="h-full deck-chart-bar rounded-full bg-blue-500" style={{
          width: `${Math.max(item.value / maxValue * 100, 12)}%`
        }} data-id="element-421" data-mp="rhhsm17pcp9x">
              <span className="flex h-full items-center justify-end pr-2 text-[10px] font-bold text-white whitespace-nowrap" data-id="element-422" data-mp="hr5asgr9va7h">
                {item.formatted}
              </span>
            </div>
          </div>
        </div>)}
    </div>;
}
// ─── Check List ──────────────────────────────────────────────────────────────
export function CheckList({
  items,
  dark = false
}: {
  items: string[];
  dark?: boolean;
}) {
  return <ul className="space-y-2.5" data-id="element-423" data-mp="1o8sdwkza05vr">
      {items.map(item => <li key={item} className={`deck-stagger-item flex gap-2.5 text-sm leading-relaxed ${dark ? 'text-slate-300' : 'text-slate-700'}`} data-id="element-424" data-mp="1o9bofgmljrzr">
          <span className="mt-2 h-1.5 w-1.5 shrink-0 rounded-full bg-blue-500" aria-hidden="true" data-id="element-425" data-mp="3dp4c8nf2tb3n" />
          {item}
        </li>)}
    </ul>;
}
// ─── Flywheel Diagram ─────────────────────────────────────────────────────────
export function FlywheelDiagram({
  dark = false
}: {
  dark?: boolean;
}) {
  const nodes = [{
    label: 'Connected\nMachines',
    angle: -90
  }, {
    label: 'Recurring\nConsumables',
    angle: -18
  }, {
    label: 'Enterprise\nDistribution',
    angle: 54
  }, {
    label: 'SaaS &\nIntelligence',
    angle: 126
  }, {
    label: 'Wellness &\nSustainability',
    angle: 198
  }];
  const r = 110;
  const cx = 160;
  const cy = 160;
  const textColor = dark ? '#94a3b8' : '#475569';
  const lineColor = dark ? 'rgba(93,173,255,0.35)' : 'rgba(77,150,255,0.25)';
  const nodeStroke = dark ? '#3b82f6' : '#4D96FF';
  const nodeFill = dark ? 'rgba(59,130,246,0.15)' : 'rgba(77,150,255,0.08)';
  return <div className="flex justify-center" aria-label="SmartSoda flywheel diagram" data-id="element-426" data-mp="2wr7bm53g124i">
      <svg viewBox="0 0 320 320" className="w-full max-w-xs md:max-w-sm" role="img" aria-label="Investment thesis flywheel: Connected Machines, Recurring Consumables, Enterprise Distribution, SaaS and Intelligence, Wellness and Sustainability orbiting SmartSoda at center" data-id="element-427" data-mp="30rq31y1fc666">
        {/* Orbit ring */}
        <circle cx={cx} cy={cy} r={r} fill="none" stroke={lineColor} strokeWidth="1.5" strokeDasharray="6 4" data-id="element-428" data-mp="3jq2e5pbwbq4e" />
        {/* Orbit markers */}
        {[0, 72, 144, 216, 288].map((deg, i) => {
        const rad = deg * Math.PI / 180;
        const x = cx + r * Math.cos(rad);
        const y = cy + r * Math.sin(rad);
        return <circle key={i} cx={x} cy={y} r="2.5" fill={nodeStroke} opacity="0.6" data-id="element-429" data-mp="77vtgqiguoso" data-mpi={i} />;
      })}
        {/* Center */}
        <circle cx={cx} cy={cy} r="38" fill={dark ? 'rgba(10,15,54,0.9)' : 'white'} stroke={nodeStroke} strokeWidth="2" data-id="element-430" data-mp="1mqtrq3wnhhuf" />
        <text x={cx} y={cy - 6} textAnchor="middle" fontSize="9" fontWeight="700" fill={dark ? '#fff' : '#0a0f36'} fontFamily="system-ui" data-id="element-431" data-mp="1cuiar0m483tn">
          SmartSoda
        </text>
        <text x={cx} y={cy + 7} textAnchor="middle" fontSize="7.5" fill={dark ? '#93c5fd' : '#4D96FF'} fontFamily="system-ui" data-id="element-432" data-mp="25kgwam13pitk">
          Infrastructure
        </text>
        <text x={cx} y={cy + 17} textAnchor="middle" fontSize="7.5" fill={dark ? '#93c5fd' : '#4D96FF'} fontFamily="system-ui" data-id="element-433" data-mp="2t7ynogggeew0">
          Platform
        </text>
        {/* Nodes */}
        {nodes.map((node, i) => {
        const rad = node.angle * Math.PI / 180;
        const nx = cx + r * Math.cos(rad);
        const ny = cy + r * Math.sin(rad);
        const lines = node.label.split('\n');
        return <g key={i} data-id="element-434" data-mp="1sr0ttq81ruch" data-mpi={i}>
              <line x1={cx} y1={cy} x2={nx} y2={ny} stroke={lineColor} strokeWidth="1" data-id="element-435" data-mp="1sslkv5v5kv5l" />
              <circle cx={nx} cy={ny} r="26" fill={nodeFill} stroke={nodeStroke} strokeWidth="1.5" data-id="element-436" data-mp="2khopimqyc7ir" />
              {lines.map((line, li) => <text key={li} x={nx} y={ny - 4 + li * 11} textAnchor="middle" fontSize="7.5" fontWeight="600" fill={textColor} fontFamily="system-ui" data-id="element-437" data-mp="v4bi2onjm3df" data-mpi={li}>
                  {line}
                </text>)}
            </g>;
      })}
      </svg>
    </div>;
}
// ─── Architecture Stack ───────────────────────────────────────────────────────
export function ArchitectureStack({
  dark = false
}: {
  dark?: boolean;
}) {
  const layers = [{
    label: 'Intelligence Layer',
    sub: 'AI · SaaS · Fleet · Wellness · Reporting',
    color: dark ? '#3b82f6' : '#4D96FF',
    textColor: '#fff'
  }, {
    label: 'Connectivity Layer',
    sub: '5G · Cloud · RFID · Remote Diagnostics',
    color: dark ? 'rgba(59,130,246,0.3)' : 'rgba(77,150,255,0.12)',
    textColor: dark ? '#93c5fd' : '#0057d1'
  }, {
    label: 'Consumables Layer',
    sub: 'Micro-dose Cartridges · Syrups · Functional Shots',
    color: dark ? 'rgba(59,130,246,0.2)' : 'rgba(77,150,255,0.07)',
    textColor: dark ? '#bfdbfe' : '#0046ab'
  }, {
    label: 'Hardware Layer',
    sub: 'JuLi 5.0 · ADA Panel · Self-contained Countertop',
    color: dark ? 'rgba(255,255,255,0.05)' : 'rgba(15,23,42,0.04)',
    textColor: dark ? '#e2e8f0' : '#0a0f36'
  }];
  return <div className="space-y-2" role="list" aria-label="SmartSoda platform architecture layers" data-id="element-438" data-mp="39ntiir21w03v">
      {layers.map((layer, i) => <div key={i} role="listitem" className="deck-stagger-item rounded-xl px-5 py-3.5 border" style={{
      backgroundColor: layer.color,
      borderColor: dark ? 'rgba(255,255,255,0.1)' : 'rgba(77,150,255,0.2)'
    }} data-id="element-439" data-mp="30m3t1qrqbb8q" data-mpi={i}>
          <div className="flex items-center justify-between" data-id="element-440" data-mp="2lxqwarkthod6">
            <span className="text-sm font-bold" style={{
          color: layer.textColor
        }} data-id="element-441" data-mp="2hv2ngjw3shg7">
              {layer.label}
            </span>
            <span className="text-xs opacity-70" style={{
          color: layer.textColor
        }} data-id="element-442" data-mp="se8f8i0pwzhx">
              {layer.sub}
            </span>
          </div>
        </div>)}
    </div>;
}
// ─── Consumables Loop ─────────────────────────────────────────────────────────
export function ConsumablesLoop({
  dark = false
}: {
  dark?: boolean;
}) {
  const steps = [{
    label: 'Hardware\nPlacement',
    icon: '⬡'
  }, {
    label: 'Recurring\nConsumables',
    icon: '↺'
  }, {
    label: 'Revenue\nPull-through',
    icon: '$'
  }, {
    label: 'Account\nRetention',
    icon: '♾'
  }];
  const cx = 140,
    cy = 140,
    r = 88;
  const nodeColor = dark ? '#3b82f6' : '#4D96FF';
  const nodeFill = dark ? 'rgba(59,130,246,0.18)' : 'rgba(77,150,255,0.1)';
  const textColor = dark ? '#cbd5e1' : '#334155';
  const arrowColor = dark ? 'rgba(93,173,255,0.5)' : 'rgba(77,150,255,0.4)';
  return <div className="flex justify-center" aria-label="Consumables recurring revenue loop" data-id="element-443" data-mp="y85nxav37279">
      <svg viewBox="0 0 280 280" className="w-full max-w-[260px]" role="img" aria-label="Loop: Hardware Placement leads to Recurring Consumables leads to Revenue Pull-through leads to Account Retention and back" data-id="element-444" data-mp="t8t1usc1hz5q">
        <circle cx={cx} cy={cy} r={r} fill="none" stroke={arrowColor} strokeWidth="1.5" strokeDasharray="5 4" data-id="element-445" data-mp="1321jk6nvocz9" />
        {steps.map((step, i) => {
        const angle = (i * 90 - 90) * (Math.PI / 180);
        const nx = cx + r * Math.cos(angle);
        const ny = cy + r * Math.sin(angle);
        const lines = step.label.split('\n');
        return <g key={i} data-id="element-446" data-mp="1zp500tev3h1b" data-mpi={i}>
              <circle cx={nx} cy={ny} r="28" fill={nodeFill} stroke={nodeColor} strokeWidth="1.5" data-id="element-447" data-mp="1zod75qcnfxeq" />
              <text x={nx} y={ny - 3} textAnchor="middle" fontSize="12" fill={nodeColor} fontFamily="system-ui" data-id="element-448" data-mp="236keyw3rn7yn">
                {step.icon}
              </text>
              {lines.map((line, li) => <text key={li} x={nx} y={ny + 10 + li * 10} textAnchor="middle" fontSize="7" fontWeight="600" fill={textColor} fontFamily="system-ui" data-id="element-449" data-mp="1yalvnmxaog53" data-mpi={li}>
                  {line}
                </text>)}
            </g>;
      })}
        {/* Center label */}
        <text x={cx} y={cy - 5} textAnchor="middle" fontSize="8" fontWeight="700" fill={dark ? '#fff' : '#0a0f36'} fontFamily="system-ui" data-id="element-450" data-mp="34wxu045vd8d5">
          Closed-Loop
        </text>
        <text x={cx} y={cy + 7} textAnchor="middle" fontSize="8" fontWeight="700" fill={dark ? '#93c5fd' : '#4D96FF'} fontFamily="system-ui" data-id="element-451" data-mp="1kzs3ant0ffm8">
          Economics
        </text>
      </svg>
    </div>;
}
// ─── Distribution Wedge ───────────────────────────────────────────────────────
export function DistributionWedge({
  dark = false
}: {
  dark?: boolean;
}) {
  const bg = dark ? 'bg-white/5 border-white/15' : 'bg-slate-50 border-slate-200';
  const accent = dark ? 'bg-blue-500/20 border-blue-400/40' : 'bg-blue-50 border-blue-300';
  const highlight = dark ? 'bg-blue-600/30 border-blue-400' : 'bg-blue-500 border-blue-600';
  return <div className="space-y-2" aria-label="Distribution wedge from SmartSoda through Canteen to enterprise accounts" data-id="element-452" data-mp="2nh3m5gckcczk">
      <div className={`deck-stagger-item rounded-xl border p-4 text-center ${highlight}`} data-id="element-453" data-mp="2ioan993rzyve">
        <p className="text-xs font-bold uppercase tracking-wider text-white" data-id="element-454" data-mp="2q2um7i353ug3">
          SmartSoda
        </p>
        <p className="text-xs text-blue-100 mt-0.5" data-id="element-455" data-mp="2v27am79nt9ks">
          Connected Hydration Platform
        </p>
      </div>
      <div className="flex justify-center" data-id="element-456" data-mp="34yam43qeikke">
        <div className="w-0.5 h-5 bg-blue-400/50" aria-hidden="true" data-id="element-457" data-mp="34zbvm3rrqz00" />
      </div>
      <div className={`deck-stagger-item rounded-xl border p-4 text-center mx-4 ${accent}`} data-id="element-458" data-mp="390nrgr18kggn">
        <p className={`text-xs font-bold uppercase tracking-wider ${dark ? 'text-blue-300' : 'text-blue-700'}`} data-id="element-459" data-mp="3dxxczvli87z7">
          Canteen / Compass
        </p>
        <p className={`text-xs mt-0.5 ${dark ? 'text-slate-400' : 'text-slate-500'}`} data-id="element-460" data-mp="3noim4cyt0uwd">
          National OCS Distribution · $36B+ Revenue · World's Largest
          Foodservice
        </p>
      </div>
      <div className="flex justify-center" data-id="element-461" data-mp="javgnt2oxyv">
        <div className="w-0.5 h-5 bg-blue-400/50" aria-hidden="true" data-id="element-462" data-mp="hzpqig2hg54" />
      </div>
      <div className={`deck-stagger-item rounded-xl border p-4 mx-8 ${bg}`} data-id="element-463" data-mp="1xl87j67mirih">
        <p className={`text-xs font-bold uppercase tracking-wider text-center mb-3 ${dark ? 'text-slate-300' : 'text-slate-600'}`} data-id="element-464" data-mp="1smomr94rnlrp">
          Enterprise Accounts
        </p>
        <div className="grid grid-cols-3 gap-2" data-id="element-465" data-mp="21m1u9w9by09s">
          {['Fortune 500\nCorporates', 'Financial\nServices', 'Healthcare\n& Pharma', 'Tech\nCampuses', 'Government\n& Institutional', 'Hospitality\n& Hotels'].map((label, i) => <div key={i} className={`rounded-lg border px-2 py-1.5 text-center ${dark ? 'border-white/10 bg-white/5' : 'border-slate-200 bg-white'}`} data-id="element-466" data-mp="2pal78p3br187" data-mpi={i}>
              {label.split('\n').map((line, li) => <p key={li} className={`text-[10px] font-medium leading-tight ${dark ? 'text-slate-400' : 'text-slate-600'}`} data-id="element-467" data-mp="qw9rvxmcw5f0" data-mpi={li}>
                  {line}
                </p>)}
            </div>)}
        </div>
      </div>
    </div>;
}
// ─── Revenue Stack ────────────────────────────────────────────────────────────
export function RevenueStack({
  dark = false
}: {
  dark?: boolean;
}) {
  const layers = [{
    label: 'Software / Service',
    sub: 'SaaS · Fleet · AI · Data · Reporting',
    pct: 20,
    color: dark ? '#3b82f6' : '#4D96FF',
    note: 'SaaS multiplier'
  }, {
    label: 'Consumables',
    sub: 'Syrups · Cartridges · Functional Shots · Coffee',
    pct: 55,
    color: dark ? '#2563eb' : '#0057d1',
    note: 'High-margin recurring'
  }, {
    label: 'Hardware',
    sub: 'Equipment Sales · Rental · Lease Models',
    pct: 25,
    color: dark ? '#1d4ed8' : '#0046ab',
    note: '20–35% gross margin'
  }];
  return <div className="space-y-2" role="list" aria-label="Three-layer revenue stack" data-id="element-468" data-mp="1ng0a45xdtd7e">
      {layers.map((layer, i) => <div key={i} role="listitem" className="deck-stagger-item rounded-xl overflow-hidden border" style={{
      borderColor: dark ? 'rgba(255,255,255,0.1)' : 'rgba(77,150,255,0.3)'
    }} data-id="element-469" data-mp="2r2e2qy92uibb" data-mpi={i}>
          <div className="flex items-stretch" data-id="element-470" data-mp="2i7zkqnx5wtoi">
            <div className="flex items-center justify-center px-4 py-4 text-white font-bold text-sm" style={{
          backgroundColor: layer.color,
          minWidth: `${layer.pct * 1.2}px`
        }} data-id="element-471" data-mp="2dbr579f1yygt">
              {layer.pct}%
            </div>
            <div className={`flex-1 px-4 py-3 ${dark ? 'bg-white/5' : 'bg-white'}`} data-id="element-472" data-mp="1zc9h6w9kzp8i">
              <p className={`text-sm font-bold ${dark ? 'text-white' : 'text-navy-950'}`} data-id="element-473" data-mp="1kdnlh9q18pj">
                {layer.label}
              </p>
              <p className={`text-xs mt-0.5 ${dark ? 'text-slate-400' : 'text-slate-500'}`} data-id="element-474" data-mp="1fg59w1zjor38">
                {layer.sub}
              </p>
            </div>
            <div className={`flex items-center px-3 ${dark ? 'bg-white/5' : 'bg-slate-50'}`} data-id="element-475" data-mp="1p6rgx7t749ab">
              <span className="text-[10px] font-semibold text-blue-500 whitespace-nowrap" data-id="element-476" data-mp="1t7bg0gxbxmz3">
                {layer.note}
              </span>
            </div>
          </div>
        </div>)}
    </div>;
}
// ─── Value Waterfall ──────────────────────────────────────────────────────────
export function ValueWaterfall({
  dark = false
}: {
  dark?: boolean;
}) {
  const steps = [{
    label: 'Equipment\nRevenue',
    value: 3,
    color: '#0046ab'
  }, {
    label: 'Monthly\nConsumables',
    value: 8,
    color: '#0057d1'
  }, {
    label: 'SaaS /\nService',
    value: 4,
    color: '#4D96FF'
  }, {
    label: 'Functional\nUpsell',
    value: 2,
    color: '#5a9dff'
  }, {
    label: 'Lifetime\nValue',
    value: 17,
    color: '#0a0f36',
    isTotal: true
  }];
  const max = 20;
  const barColor = (s: (typeof steps)[0]) => s.isTotal ? dark ? '#fff' : '#0a0f36' : s.color;
  const textColor = dark ? '#cbd5e1' : '#475569';
  return <div aria-label="Unit economics value waterfall" data-id="element-477" data-mp="3224cy8oakk3a">
      <div className="flex items-end gap-2 h-32" data-id="element-478" data-mp="24fxap5fgn3u3">
        {steps.map((step, i) => {
        const heightPct = step.value / max * 100;
        const lines = step.label.split('\n');
        return <div key={i} className="flex-1 flex flex-col items-center gap-1" data-id="element-479" data-mp="2ed1w331hev9u" data-mpi={i}>
              {i < steps.length - 1 && <div className="w-full flex justify-end pr-1" data-id="element-480" data-mp="2jdzgzndvm3f1">
                  <span className="text-[9px] text-blue-400" data-id="element-481" data-mp="3pt0371d3n78f">+</span>
                </div>}
              <div className="w-full flex flex-col justify-end" style={{
            height: '96px'
          }} data-id="element-482" data-mp="3uqs6erj72frx">
                <div className="deck-chart-bar w-full rounded-t-md flex items-end justify-center pb-1" style={{
              height: `${heightPct}%`,
              backgroundColor: barColor(step),
              minHeight: '16px'
            }} data-id="element-483" data-mp="1e552lhjyqd8t">
                  <span className="text-[9px] font-bold text-white" data-id="element-484" data-mp="zgh6992alzht">
                    ${step.value}K
                  </span>
                </div>
              </div>
              {lines.map((line, li) => <p key={li} className="text-[9px] font-medium text-center leading-tight" style={{
            color: textColor
          }} data-id="element-485" data-mp="qgltx0x03yg7" data-mpi={li}>
                  {line}
                </p>)}
            </div>;
      })}
      </div>
      <div className={`mt-3 rounded-xl border px-4 py-2.5 text-center ${dark ? 'border-white/15 bg-white/5' : 'border-slate-200 bg-slate-50'}`} data-id="element-486" data-mp="2fse2ix72bxhv">
        <span className={`text-sm font-bold ${dark ? 'text-white' : 'text-navy-950'}`} data-id="element-487" data-mp="ix2yfv6kmcqq">
          $15K–$30K lifetime value per system
        </span>
        <span className={`ml-2 text-xs ${dark ? 'text-slate-400' : 'text-slate-500'}`} data-id="element-488" data-mp="95yk8xopyvwy">
          depending on channel and model
        </span>
      </div>
    </div>;
}
// ─── Network Hub ──────────────────────────────────────────────────────────────
export function NetworkHub({
  dark = false
}: {
  dark?: boolean;
}) {
  const spokes = ['Foodservice\nDistributors', 'OCS\nPlatforms', 'Beverage\nCompanies', 'Coffee\nCompanies', 'Water\nCompanies', 'Workplace\nWellness', 'Vending /\nMicro-market', 'Connected\nAppliances', 'Sustainability\nInfrastructure', 'Facilities\nPlatforms'];
  const cx = 180,
    cy = 180,
    r = 140;
  const nodeColor = dark ? '#3b82f6' : '#4D96FF';
  const nodeFill = dark ? 'rgba(59,130,246,0.15)' : 'rgba(77,150,255,0.08)';
  const lineColor = dark ? 'rgba(93,173,255,0.3)' : 'rgba(77,150,255,0.2)';
  const textColor = dark ? '#94a3b8' : '#475569';
  return <div className="flex justify-center" aria-label="Strategic acquirer hub-and-spoke diagram" data-id="element-489" data-mp="23pgr2vkt58ew">
      <svg viewBox="0 0 360 360" className="w-full max-w-sm" role="img" aria-label="SmartSoda at center with 10 strategic acquirer categories as spokes" data-id="element-490" data-mp="1zmajnklylb6u">
        {spokes.map((spoke, i) => {
        const angle = (i * 36 - 90) * (Math.PI / 180);
        const nx = cx + r * Math.cos(angle);
        const ny = cy + r * Math.sin(angle);
        const lines = spoke.split('\n');
        return <g key={i} data-id="element-491" data-mp="sabn7o8xx3tp" data-mpi={i}>
              <line x1={cx} y1={cy} x2={nx} y2={ny} stroke={lineColor} strokeWidth="1.5" data-id="element-492" data-mp="s9ikcyd3qygu" />
              <circle cx={nx} cy={ny} r="24" fill={nodeFill} stroke={nodeColor} strokeWidth="1.2" data-id="element-493" data-mp="10zs7op4cq58z" />
              {lines.map((line, li) => <text key={li} x={nx} y={ny - 3 + li * 10} textAnchor="middle" fontSize="6.5" fontWeight="600" fill={textColor} fontFamily="system-ui" data-id="element-494" data-mp="1fn3keuc9rvao" data-mpi={li}>
                  {line}
                </text>)}
            </g>;
      })}
        {/* Center */}
        <circle cx={cx} cy={cy} r="42" fill={dark ? '#0a0f36' : 'white'} stroke={nodeColor} strokeWidth="2.5" data-id="element-495" data-mp="1yl6gft38ishz" />
        <text x={cx} y={cy - 8} textAnchor="middle" fontSize="10" fontWeight="800" fill={dark ? '#fff' : '#0a0f36'} fontFamily="system-ui" data-id="element-496" data-mp="47oozuzplbho">
          SmartSoda
        </text>
        <text x={cx} y={cy + 5} textAnchor="middle" fontSize="7.5" fill={dark ? '#93c5fd' : '#4D96FF'} fontFamily="system-ui" data-id="element-497" data-mp="3o1a1w3mwwseo">
          Platform
        </text>
        <text x={cx} y={cy + 16} textAnchor="middle" fontSize="7.5" fill={dark ? '#93c5fd' : '#4D96FF'} fontFamily="system-ui" data-id="element-498" data-mp="fn9vje2ekjad">
          Asset
        </text>
      </svg>
    </div>;
}
// ─── Timeline Roadmap ─────────────────────────────────────────────────────────
export function TimelineRoadmap({
  dark = false
}: {
  dark?: boolean;
}) {
  const phases = [{
    year: '2024',
    label: 'Foundation',
    detail: 'Early traction · Pulp / Sysco · Initial deployments',
    active: false
  }, {
    year: '2025',
    label: 'Profitability',
    detail: 'JuLi 5.0 dev · Compass PO · AI app · Pulp expansion',
    active: false
  }, {
    year: '2026',
    label: 'Activation',
    detail: 'JuLi 5.0 launch · Canteen rollout · Israel · M&A',
    active: true
  }, {
    year: '2027',
    label: 'Global Scale',
    detail: 'SaaS ecosystem · Europe · Asia-Pacific',
    active: false
  }, {
    year: '2028–30',
    label: 'Liquidity',
    detail: 'IPO / strategic exit · ANZ · Middle East · Global',
    active: false
  }];
  return <div className="overflow-x-auto pb-2" aria-label="Company roadmap timeline" data-id="element-499" data-mp="ylmutehnwypw">
      <div className="relative min-w-[720px]" data-id="element-500" data-mp="ku88oysgkhem">
        {/* Horizontal line */}
        <div className={`absolute top-8 left-0 right-0 h-0.5 ${dark ? 'bg-white/15' : 'bg-slate-200'}`} aria-hidden="true" data-id="element-501" data-mp="ps9p1byi22me" />
        <div className="relative flex justify-between" data-id="element-502" data-mp="ft12h45yrqa2">
          {phases.map((phase, i) => <div key={i} className="deck-stagger-item flex flex-col items-center" style={{
          width: '18%'
        }} data-id="element-503" data-mp="3scmykixydlnm" data-mpi={i}>
              {/* Node */}
              <div className={`relative z-10 w-4 h-4 rounded-full border-2 mb-3 ${phase.active ? 'bg-blue-500 border-blue-500' : dark ? 'bg-navy-950 border-white/30' : 'bg-white border-slate-300'}`} aria-hidden="true" data-id="element-504" data-mp="65ygzdzd6xdj" />
              <div className={`rounded-xl border p-3 w-full text-center ${phase.active ? dark ? 'border-blue-400/50 bg-blue-500/15' : 'border-blue-400 bg-blue-50' : dark ? 'border-white/10 bg-white/5' : 'border-slate-200 bg-white'}`} data-id="element-505" data-mp="b4rjych0z5zg">
                <p className={`text-xs font-bold ${dark ? 'text-blue-300' : 'text-blue-600'}`} data-id="element-506" data-mp="jxv5z95qkr5p">
                  {phase.year}
                </p>
                <p className={`text-xs font-bold mt-0.5 ${dark ? 'text-white' : 'text-navy-950'}`} data-id="element-507" data-mp="twkc64o9sn64">
                  {phase.label}
                </p>
                <p className={`text-[10px] mt-1 leading-relaxed ${dark ? 'text-slate-400' : 'text-slate-500'}`} data-id="element-508" data-mp="3fme2qbezosq0">
                  {phase.detail}
                </p>
              </div>
            </div>)}
        </div>
      </div>
    </div>;
}
// ─── Data Flow Diagram ────────────────────────────────────────────────────────
export function DataFlowDiagram({
  dark = false
}: {
  dark?: boolean;
}) {
  // Nodes: left column (inputs), center (platform), right column (outputs)
  const inputs = ['Installed\nMachines', 'User\nBehavior', 'Consumables\nUsage', 'POS /\nPayment'];
  const outputs = ['Personalized\nHydration', 'Automated\nInventory', 'Corporate\nReporting', 'Service\nOptimization'];
  const nodeColor = '#3b82f6';
  const nodeFill = 'rgba(59,130,246,0.18)';
  const lineColor = 'rgba(93,173,255,0.4)';
  const textColor = dark ? '#94a3b8' : '#475569';
  return <div className="flex justify-center" aria-label="Intelligence layer data flow diagram" data-id="element-509" data-mp="v8zcxld92cni">
      <svg viewBox="0 0 420 240" className="w-full max-w-lg" role="img" aria-label="Data flows from machines and users through the AI platform to personalization and reporting outputs" data-id="element-510" data-mp="mancysqhu2ie">
        {/* Input nodes */}
        {inputs.map((label, i) => {
        const y = 30 + i * 52;
        const lines = label.split('\n');
        return <g key={`in-${i}`} data-id="element-511" data-mp="7jwkdih2uze8" data-mpi={i}>
              <rect x="8" y={y} width="72" height="36" rx="8" fill={nodeFill} stroke={nodeColor} strokeWidth="1.2" data-id="element-512" data-mp="3jme04pdi6yu" />
              {lines.map((line, li) => <text key={li} x="44" y={y + 14 + li * 12} textAnchor="middle" fontSize="7.5" fontWeight="600" fill={textColor} fontFamily="system-ui" data-id="element-513" data-mp="3krzo35l52wtv" data-mpi={li}>
                  {line}
                </text>)}
              <line x1="80" y1={y + 18} x2="148" y2="120" stroke={lineColor} strokeWidth="1.2" strokeDasharray="4 3" data-id="element-514" data-mp="2ypsxryv8x9f7" />
            </g>;
      })}
        {/* Center platform */}
        <rect x="148" y="88" width="124" height="64" rx="12" fill="rgba(59,130,246,0.3)" stroke={nodeColor} strokeWidth="2" data-id="element-515" data-mp="2kqbgs5o6z0ge" />
        <text x="210" y="113" textAnchor="middle" fontSize="9" fontWeight="800" fill="#fff" fontFamily="system-ui" data-id="element-516" data-mp="1s8pexi0d7e84">
          AI / SaaS
        </text>
        <text x="210" y="126" textAnchor="middle" fontSize="8" fill="#93c5fd" fontFamily="system-ui" data-id="element-517" data-mp="2qsdh1k6phqr">
          Intelligence Platform
        </text>
        <text x="210" y="139" textAnchor="middle" fontSize="7.5" fill="#bfdbfe" fontFamily="system-ui" data-id="element-518" data-mp="3puymg3ee9txk">
          IoT · Data · Analytics
        </text>
        {/* Output nodes */}
        {outputs.map((label, i) => {
        const y = 30 + i * 52;
        const lines = label.split('\n');
        return <g key={`out-${i}`} data-id="element-519" data-mp="chwv6918vgpa" data-mpi={i}>
              <line x1="272" y1="120" x2="340" y2={y + 18} stroke={lineColor} strokeWidth="1.2" strokeDasharray="4 3" data-id="element-520" data-mp="hh9jky7rkvtz" />
              <rect x="340" y={y} width="72" height="36" rx="8" fill={nodeFill} stroke={nodeColor} strokeWidth="1.2" data-id="element-521" data-mp="3289wcuf8ev5v" />
              {lines.map((line, li) => <text key={li} x="376" y={y + 14 + li * 12} textAnchor="middle" fontSize="7.5" fontWeight="600" fill={textColor} fontFamily="system-ui" data-id="element-522" data-mp="29bhitrm1n0y9" data-mpi={li}>
                  {line}
                </text>)}
            </g>;
      })}
      </svg>
    </div>;
}
// ─── Converging Tails ─────────────────────────────────────────────────────────
export function ConvergingTails({
  dark = false
}: {
  dark?: boolean;
}) {
  const textColor = dark ? '#94a3b8' : '#64748b';
  const line1Color = dark ? '#3b82f6' : '#4D96FF';
  const line2Color = dark ? '#60a5fa' : '#0057d1';
  const nowColor = dark ? '#fff' : '#0a0f36';
  return <div aria-label="Why now: converging market trends diagram" data-id="element-523" data-mp="3nbub21pfkuue">
      <svg viewBox="0 0 500 180" className="w-full" role="img" aria-label="Two converging trend lines: enterprise demand for connected infrastructure and SmartSoda's readiness, meeting at 'Now'" data-id="element-524" data-mp="3sb6xwmgay1wz">
        {/* Grid lines */}
        {[0, 1, 2, 3].map(i => <line key={i} x1="40" y1={20 + i * 40} x2="460" y2={20 + i * 40} stroke={dark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.05)'} strokeWidth="1" data-id="element-525" data-mp="34eb0gtnbr45v" />)}
        {/* X axis */}
        <line x1="40" y1="140" x2="460" y2="140" stroke={dark ? 'rgba(255,255,255,0.2)' : 'rgba(0,0,0,0.15)'} strokeWidth="1" data-id="element-526" data-mp="3idar26uedt4o" />
        {/* Year labels */}
        {['2022', '2023', '2024', '2025', '2026 →'].map((yr, i) => <text key={i} x={40 + i * 105} y="158" textAnchor="middle" fontSize="8" fill={textColor} fontFamily="system-ui" data-id="element-527" data-mp="w66ap183slsd" data-mpi={i}>
            {yr}
          </text>)}
        {/* Line 1: Enterprise demand rising */}
        <polyline points="40,120 145,100 250,78 355,52 460,28" fill="none" stroke={line1Color} strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" data-id="element-528" data-mp="115ix5kv2bquf" />
        {/* Line 2: SmartSoda readiness rising */}
        <polyline points="40,130 145,118 250,100 355,72 460,38" fill="none" stroke={line2Color} strokeWidth="2" strokeDasharray="6 3" strokeLinecap="round" strokeLinejoin="round" data-id="element-529" data-mp="1a4345or9gc2t" />
        {/* "Now" marker */}
        <line x1="355" y1="20" x2="355" y2="140" stroke={dark ? 'rgba(255,255,255,0.3)' : 'rgba(0,0,0,0.2)'} strokeWidth="1" strokeDasharray="4 3" data-id="element-530" data-mp="1xtffova28w8a" />
        <text x="355" y="16" textAnchor="middle" fontSize="9" fontWeight="700" fill={nowColor} fontFamily="system-ui" data-id="element-531" data-mp="2hh1qqquth577">
          NOW
        </text>
        {/* Legend */}
        <line x1="48" y1="170" x2="72" y2="170" stroke={line1Color} strokeWidth="2.5" data-id="element-532" data-mp="28f2ai0rmm55b" />
        <text x="76" y="173" fontSize="8" fill={textColor} fontFamily="system-ui" data-id="element-533" data-mp="1yiz9iy8igcjy">
          Enterprise demand for connected infrastructure
        </text>
        <line x1="260" y1="170" x2="284" y2="170" stroke={line2Color} strokeWidth="2" strokeDasharray="5 3" data-id="element-534" data-mp="1orurfpnjgbkw" />
        <text x="288" y="173" fontSize="8" fill={textColor} fontFamily="system-ui" data-id="element-535" data-mp="1fshhywzezn06">
          SmartSoda deployment readiness
        </text>
      </svg>
    </div>;
}
// ─── Allocation Bar ───────────────────────────────────────────────────────────
export function AllocationBar({
  dark = false
}: {
  dark?: boolean;
}) {
  const allocations = [{
    label: 'Inventory & Working Capital',
    pct: 30,
    color: '#0a0f36'
  }, {
    label: 'JuLi 5.0 Production',
    pct: 22,
    color: '#0046ab'
  }, {
    label: 'Compass Rollout Support',
    pct: 18,
    color: '#0057d1'
  }, {
    label: 'Sales & Channel',
    pct: 12,
    color: '#4D96FF'
  }, {
    label: 'AI / SaaS Layer',
    pct: 10,
    color: '#5a9dff'
  }, {
    label: 'M&A Readiness',
    pct: 8,
    color: '#8ebeff'
  }];
  return <div className="space-y-3" aria-label="Use of funds allocation" data-id="element-536" data-mp="2g1pjiuhevyy4">
      {/* Stacked bar */}
      <div className="flex h-8 rounded-xl overflow-hidden" role="img" aria-label="Proportional allocation bar" data-id="element-537" data-mp="2azgsw4s0h8z6">
        {allocations.map((a, i) => <div key={i} className="h-full flex items-center justify-center" style={{
        width: `${a.pct}%`,
        backgroundColor: a.color
      }} title={`${a.label}: ${a.pct}%`} data-id="element-538" data-mp="25wzgsme6oqsu" data-mpi={i}>
            {a.pct >= 15 && <span className="text-[9px] font-bold text-white" data-id="element-539" data-mp="2jmkv3jh1ghuj">{a.pct}%</span>}
          </div>)}
      </div>
      {/* Legend */}
      <div className="grid grid-cols-2 gap-2 md:grid-cols-3" data-id="element-540" data-mp="2ooifwi537kpl">
        {allocations.map((a, i) => <div key={i} className={`deck-stagger-item flex items-center gap-2 rounded-lg border px-3 py-2 ${dark ? 'border-white/10 bg-white/5' : 'border-slate-200 bg-white'}`} data-id="element-541" data-mp="2ykv9zh4jo0kv" data-mpi={i}>
            <div className="h-3 w-3 shrink-0 rounded-sm" style={{
          backgroundColor: a.color
        }} aria-hidden="true" data-id="element-542" data-mp="146kj734e2wcg" />
            <div data-id="element-543" data-mp="315kub135u7dc">
              <p className={`text-[10px] font-semibold ${dark ? 'text-slate-300' : 'text-slate-700'}`} data-id="element-544" data-mp="316312rng83zi">
                {a.label}
              </p>
              <p className="text-[10px] font-bold text-blue-500" data-id="element-545" data-mp="3avcwagdla2ru">{a.pct}%</p>
            </div>
          </div>)}
      </div>
    </div>;
}
// ─── Impact Conversion ────────────────────────────────────────────────────────
export function ImpactConversion({
  dark = false
}: {
  dark?: boolean;
}) {
  return <div className="space-y-4" aria-label="Sustainability impact conversion visual" data-id="element-546" data-mp="2w6q6tlu8zumw">
      {/* Big number row */}
      <div className="deck-stagger grid grid-cols-3 gap-3" data-id="element-547" data-mp="2s31wive8ekxy">
        {[{
        value: '35,000',
        label: 'Bottles saved / year',
        sub: 'Per JuLi dispenser'
      }, {
        value: '2.3B',
        label: 'Projected by 2028',
        sub: 'Bottles eliminated'
      }, {
        value: '90%+',
        label: 'Plastic reduction',
        sub: 'Per deployment'
      }].map((m, i) => <div key={i} className={`deck-stagger-item rounded-xl border p-4 text-center ${dark ? 'border-white/15 bg-white/5' : 'border-green-200 bg-green-50'}`} data-id="element-548" data-mp="510tqqrwulqw" data-mpi={i}>
            <p className={`text-2xl font-bold ${dark ? 'text-white' : 'text-green-700'}`} data-id="element-549" data-mp="xppnu5ujtqt5">
              {m.value}
            </p>
            <p className={`text-xs font-semibold mt-1 ${dark ? 'text-slate-300' : 'text-green-800'}`} data-id="element-550" data-mp="nyuzmvxqsh52">
              {m.label}
            </p>
            <p className={`text-[10px] mt-0.5 ${dark ? 'text-slate-500' : 'text-green-600'}`} data-id="element-551" data-mp="svlfpj9jrcqh">
              {m.sub}
            </p>
          </div>)}
      </div>
      {/* Conversion visual */}
      <div className="deck-stagger-item grid grid-cols-2 gap-3" data-id="element-552" data-mp="2l2pcm6ck3og0">
        <div className={`rounded-xl border p-4 ${dark ? 'border-red-400/20 bg-red-400/5' : 'border-red-100 bg-red-50'}`} data-id="element-553" data-mp="2l4k09sf7puns">
          <p className={`text-xs font-bold mb-2 ${dark ? 'text-red-300' : 'text-red-700'}`} data-id="element-554" data-mp="2b4t47mb077ll">
            Traditional beverage model
          </p>
          <CheckList dark={dark} items={['Plastic bottles & cans', 'High logistics volume', 'Large storage footprint', 'No waste reporting', 'No ESG data']} data-id="element-555" data-mp="2g57e79w60rm9" />
        </div>
        <div className={`rounded-xl border p-4 ${dark ? 'border-green-400/20 bg-green-400/5' : 'border-green-200 bg-green-50'}`} data-id="element-556" data-mp="2u11dr2b1u4mp">
          <p className={`text-xs font-bold mb-2 ${dark ? 'text-green-300' : 'text-green-700'}`} data-id="element-557" data-mp="3mrji06niec1p">
            SmartSoda infrastructure model
          </p>
          <CheckList dark={dark} items={['90%+ plastic eliminated', 'Micro-dose cartridges', 'Minimal storage', 'Real-time ESG reporting', 'Procurement says yes']} data-id="element-558" data-mp="1g3apyiz9aa4k" />
        </div>
      </div>
    </div>;
}
// ─── Wellness Matrix ──────────────────────────────────────────────────────────
export function WellnessMatrix({
  dark = false
}: {
  dark?: boolean;
}) {
  const categories = [{
    cat: 'Hydration',
    items: ['Alkaline water', 'Sparkling water', 'Electrolytes']
  }, {
    cat: 'Energy',
    items: ['Cold brew coffee', 'Energy shots', 'B-vitamin infused']
  }, {
    cat: 'Immunity',
    items: ['Vitamin C shots', 'Zinc infused', 'Prebiotic options']
  }, {
    cat: 'Wellness',
    items: ['Low-sugar options', 'Functional shots', 'Clean-label flavors']
  }];
  return <div className="grid grid-cols-2 gap-3 md:grid-cols-4" aria-label="Functional hydration wellness matrix" data-id="element-559" data-mp="2ckx5uq69s5jd">
      {categories.map((cat, i) => <div key={i} className={`deck-stagger-item rounded-xl border p-4 ${dark ? 'border-white/15 bg-white/5' : 'border-slate-200 bg-white shadow-sm'}`} data-id="element-560" data-mp="1f6ca85cte8bu" data-mpi={i}>
          <p className={`text-xs font-bold uppercase tracking-wider mb-3 ${dark ? 'text-blue-300' : 'text-blue-600'}`} data-id="element-561" data-mp="1o7jxzthpfqro">
            {cat.cat}
          </p>
          <ul className="space-y-1.5" data-id="element-562" data-mp="rfq44jmdey4h">
            {cat.items.map((item, j) => <li key={j} className={`flex items-center gap-2 text-xs ${dark ? 'text-slate-300' : 'text-slate-600'}`} data-id="element-563" data-mp="vh1uird2jndq" data-mpi={j}>
                <span className="h-1 w-1 rounded-full bg-blue-500 shrink-0" aria-hidden="true" data-id="element-564" data-mp="159pq5isbblav" />
                {item}
              </li>)}
          </ul>
        </div>)}
    </div>;
}
// ─── SIP Layer Diagram ────────────────────────────────────────────────────────
export function SIPLayerDiagram({
  dark = false
}: {
  dark?: boolean;
}) {
  return <div className="space-y-2" aria-label="SIP as supporting intelligence layer beneath SmartSoda" data-id="element-565" data-mp="au6m6006qte">
      {/* SmartSoda primary layer */}
      <div className="deck-stagger-item rounded-xl border-2 border-blue-500 bg-blue-500 p-4 text-white text-center" data-id="element-566" data-mp="3nfa4st7fp64u">
        <p className="text-sm font-bold uppercase tracking-wider" data-id="element-567" data-mp="3igpa19aybens">SmartSoda</p>
        <p className="text-xs text-blue-100 mt-0.5" data-id="element-568" data-mp="3dofe9cn5nkdl">
          Physical network · Machines · Consumables · Distribution · Enterprise
          accounts
        </p>
      </div>
      {/* Connector */}
      <div className="flex justify-center items-center gap-3" aria-hidden="true" data-id="element-569" data-mp="33r9me26pvpcb">
        <div className={`flex-1 h-px ${dark ? 'bg-white/15' : 'bg-slate-200'}`} data-id="element-570" data-mp="2zpfrkaj7dfk2" />
        <div className={`text-xs font-semibold px-3 py-1 rounded-full border ${dark ? 'border-blue-400/40 text-blue-300 bg-blue-400/10' : 'border-blue-200 text-blue-600 bg-blue-50'}`} data-id="element-571" data-mp="2uptdxzzgpxaq">
          powered by
        </div>
        <div className={`flex-1 h-px ${dark ? 'bg-white/15' : 'bg-slate-200'}`} data-id="element-572" data-mp="12nqmx4i09z49" />
      </div>
      {/* SIP secondary layer */}
      <div className={`deck-stagger-item rounded-xl border p-4 ${dark ? 'border-blue-400/30 bg-blue-400/10' : 'border-blue-200 bg-blue-50'}`} data-id="element-573" data-mp="tvopzg75b0yq">
        <p className={`text-sm font-bold text-center ${dark ? 'text-blue-300' : 'text-blue-700'}`} data-id="element-574" data-mp="f122oc5ixukh">
          SIP Technologies
        </p>
        <p className={`text-xs text-center mt-0.5 ${dark ? 'text-slate-400' : 'text-slate-500'}`} data-id="element-575" data-mp="5zaxr9it5rro">
          Intelligence layer · AI recommendations · App engagement · Fleet
          dashboards · Analytics
        </p>
        <div className="mt-3 grid grid-cols-3 gap-2" data-id="element-576" data-mp="20d26m6tbc7m2">
          {['AI Hydration\nRecommendations', 'Service\nAutomation', 'Wellness &\nESG Reporting', 'Account\nIntelligence', 'Consumables\nOptimization', 'Future\nLicensing'].map((item, i) => <div key={i} className={`rounded-lg border px-2 py-1.5 text-center ${dark ? 'border-white/10 bg-white/5' : 'border-blue-100 bg-white'}`} data-id="element-577" data-mp="2o7dm37jqyyqx" data-mpi={i}>
              {item.split('\n').map((line, li) => <p key={li} className={`text-[9px] font-medium leading-tight ${dark ? 'text-slate-400' : 'text-slate-600'}`} data-id="element-578" data-mp="2y48i9jsjsn21" data-mpi={li}>
                  {line}
                </p>)}
            </div>)}
        </div>
      </div>
      {/* Value unlock */}
      <div className={`deck-stagger-item rounded-xl border px-4 py-3 text-center ${dark ? 'border-white/10 bg-white/5' : 'border-slate-200 bg-slate-50'}`} data-id="element-579" data-mp="332s65pqpc8x1">
        <p className={`text-xs font-semibold ${dark ? 'text-slate-300' : 'text-slate-600'}`} data-id="element-580" data-mp="18s6nyc4h530h">
          SmartSoda owns the network.{' '}
          <span className="text-blue-500" data-id="element-581" data-mp="3cugcrlif7ir0">SIP makes the network compound.</span>
        </p>
      </div>
    </div>;
}
// ─── Proof Dashboard ──────────────────────────────────────────────────────────
export function ProofDashboard({
  dark = false
}: {
  dark?: boolean;
}) {
  const categories = [{
    cat: 'Deployment',
    color: 'blue',
    metrics: ['Signed agreements', 'Purchase orders', 'Units shipped', 'Units deployed', 'Install cost']
  }, {
    cat: 'Revenue',
    color: 'indigo',
    metrics: ['Revenue by machine', 'Consumables / machine', 'Gross margin by stream', 'MRR / SaaS contribution', 'Cash conversion cycle']
  }, {
    cat: 'Operations',
    color: 'slate',
    metrics: ['Machine uptime', 'Service ticket rate', 'Payback period', 'Reorder frequency', 'Working capital need']
  }, {
    cat: 'Growth',
    color: 'sky',
    metrics: ['Pipeline by partner', 'App users', 'Active users', 'Consumption lift', 'Expansion per account']
  }];
  const colorMap: Record<string, string> = {
    blue: 'text-blue-500 border-blue-200 bg-blue-50',
    indigo: 'text-indigo-500 border-indigo-200 bg-indigo-50',
    slate: 'text-slate-500 border-slate-200 bg-slate-50',
    sky: 'text-sky-500 border-sky-200 bg-sky-50'
  };
  const darkColorMap: Record<string, string> = {
    blue: 'text-blue-400 border-blue-400/20 bg-blue-400/10',
    indigo: 'text-indigo-400 border-indigo-400/20 bg-indigo-400/10',
    slate: 'text-slate-400 border-white/10 bg-white/5',
    sky: 'text-sky-400 border-sky-400/20 bg-sky-400/10'
  };
  return <div className="grid grid-cols-2 gap-3 md:grid-cols-4" aria-label="Investor proof metrics dashboard" data-id="element-582" data-mp="30kgyeg5ehj7s">
      {categories.map((cat, i) => <div key={i} className={`deck-stagger-item rounded-xl border p-3 ${dark ? darkColorMap[cat.color] : colorMap[cat.color]}`} data-id="element-583" data-mp="28vcntk3wqnhj" data-mpi={i}>
          <p className={`text-[10px] font-bold uppercase tracking-wider mb-2 ${dark ? darkColorMap[cat.color].split(' ')[0] : colorMap[cat.color].split(' ')[0]}`} data-id="element-584" data-mp="1zwr43lmp92f6">
            {cat.cat}
          </p>
          <ul className="space-y-1.5" data-id="element-585" data-mp="2roox354mujr1">
            {cat.metrics.map((m, j) => <li key={j} className={`flex items-center gap-1.5 text-[10px] ${dark ? 'text-slate-400' : 'text-slate-600'}`} data-id="element-586" data-mp="2wmqfdnu8if1g" data-mpi={j}>
                <span className="h-1 w-1 rounded-full bg-current shrink-0" aria-hidden="true" data-id="element-587" data-mp="2nljul2kjyi2e" />
                {m}
              </li>)}
          </ul>
        </div>)}
    </div>;
}